import type {
	HookStateFunction,
	OverrideContainerComponent,
	StoreActionApi,
} from 'react-sweet-state';
import {
	createStore,
	createStateHook,
	createActionsHook,
	createContainer,
} from 'react-sweet-state';

import type { AvailableSite } from '@atlassian/gic-anywhere/utils';

import type {
	FieldMetadata,
	JiraComponents,
	JiraIssueKeysToTypes,
	JiraProject,
	JiraProjects,
	SimpleJiraIssuesSet,
} from '../__types__/apiUtils.ts';

import type { IssueType } from './BulkCreateContextProvider.tsx';

export enum FieldType {
	PARAGRAPH,
	SHORT_TEXT,
	NUMBER,
	DATE,
	TIMESTAMP,
	USER,
	PEOPLE,
	TEAM,
	PRIORITY,
	COMPONENTS,
	LABELS,
	VERSIONS,
	PARENT,
	UNSUPPORTED,
	SINGLE_SELECT,
	MULTI_SELECT,
}

export type ValidatedIssueFieldValues = {
	canBeRequired: boolean;
	fieldValues: Array<any>;
};

export type Column = {
	index: number;
	header: string;
};

export type ColumnToFieldMapping = {
	column: Column | undefined;
	field: FieldMetadata | undefined;
};

type MappingValidationError = {
	message: string;
};

export type UnsavedColumnToFieldMapping = ColumnToFieldMapping & {
	isDefaultMapping: boolean;
	errors: {
		column?: MappingValidationError;
		field?: MappingValidationError;
	};
};

type BulkConfigureState = {
	optionsAvailableSites: AvailableSite[] | null;
	optionsRecentProjects: JiraProjects | null;
	optionsAllProjects: JiraProjects | null;
	optionsIssueTypes: IssueType[] | null;
	optionsIssueTypeFields: FieldMetadata[] | null;
	optionsColumns: Column[] | null;
	selectedAvailableSite: AvailableSite | null;
	selectedProject: JiraProject | null;
	selectedIssueType: IssueType | null;
	loadingProjects: boolean;
	loadingIssueType: boolean;
	loadingIssueTypeFields: boolean;
	projectSelectorQuery: string;
	isJiraError: boolean;
	columnToFieldMappings: ColumnToFieldMapping[] | null;
	unsavedColumnToFieldMappings: UnsavedColumnToFieldMapping[] | null;
	columnOffset: number | null;
	mappingWorkItemTypes: JiraIssueKeysToTypes | null;
	mappingValidParentCandidates: SimpleJiraIssuesSet | null;
	mappingFieldAllowedValues: Map<string, unknown[]> | null;
	mappingAvailableComponents: JiraComponents | null;
};

const initialState: BulkConfigureState = {
	optionsAvailableSites: null,
	optionsRecentProjects: null,
	optionsAllProjects: null,
	optionsIssueTypes: null,
	optionsIssueTypeFields: null,
	optionsColumns: null,
	selectedAvailableSite: null,
	selectedProject: null,
	selectedIssueType: null,
	loadingProjects: false,
	loadingIssueType: false,
	loadingIssueTypeFields: false,
	projectSelectorQuery: '',
	isJiraError: false,
	columnToFieldMappings: null,
	unsavedColumnToFieldMappings: null,
	columnOffset: null,
	mappingWorkItemTypes: null,
	mappingValidParentCandidates: null,
	mappingFieldAllowedValues: null,
	mappingAvailableComponents: null,
};

const actions = {
	setOptionsAvailableSites:
		(optionsAvailableSites: AvailableSite[]) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ optionsAvailableSites });
		},
	setOptionsRecentProjects:
		(optionsRecentProjects: JiraProjects) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ optionsRecentProjects });
		},
	setOptionsAllProjects:
		(optionsAllProjects: JiraProjects) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ optionsAllProjects });
		},
	setOptionsIssueTypes:
		(optionsIssueTypes: IssueType[]) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ optionsIssueTypes });
		},
	setOptionsIssueTypeFields:
		(optionsIssueTypeFields: FieldMetadata[] | null) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ optionsIssueTypeFields });
		},
	setOptionsColumns:
		(optionsColumns: Column[] | null) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ optionsColumns });
		},
	setSelectedAvailableSite:
		(selectedAvailableSite: AvailableSite) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({
				selectedAvailableSite,
				selectedProject: null,
				selectedIssueType: null,
				optionsRecentProjects: null,
				optionsAllProjects: null,
				optionsIssueTypes: null,
				optionsIssueTypeFields: null,
				isJiraError: false,
			});
		},
	setSelectedProject:
		(selectedProject: JiraProject) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({
				selectedProject,
				selectedIssueType: null,
				optionsIssueTypes: null,
				optionsIssueTypeFields: null,
			});
		},
	setSelectedProjectIfEmpty:
		(newSelectedProject: JiraProject) =>
		({ getState, setState }: StoreActionApi<BulkConfigureState>) => {
			const { selectedProject } = getState();
			if (!selectedProject) {
				setState({
					selectedProject: newSelectedProject,
					selectedIssueType: null,
					optionsIssueTypes: null,
					optionsIssueTypeFields: null,
				});
			}
		},
	setSelectedIssueType:
		(selectedIssueType: IssueType) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ selectedIssueType, optionsIssueTypeFields: null });
		},
	setSelectedIssueTypeIfEmpty:
		(newSelectedIssueType: IssueType) =>
		({ getState, setState }: StoreActionApi<BulkConfigureState>) => {
			const { selectedIssueType } = getState();
			if (!selectedIssueType) {
				setState({
					selectedIssueType: newSelectedIssueType,
					optionsIssueTypeFields: null,
				});
			}
		},
	setLoadingProjects:
		(loadingProjects: boolean) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ loadingProjects });
		},
	setLoadingIssueType:
		(loadingIssueType: boolean) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ loadingIssueType });
		},
	setLoadingIssueTypeFields:
		(loadingIssueTypeFields: boolean) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ loadingIssueTypeFields });
		},
	setProjectSelectorQuery:
		(projectSelectorQuery: string) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ projectSelectorQuery });
		},
	setIsJiraError:
		(isJiraError: boolean) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ isJiraError });
		},
	setColumnToFieldMappings:
		(columnToFieldMappings: ColumnToFieldMapping[] | null) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ columnToFieldMappings });
		},
	setUnsavedColumnToFieldMappings:
		(unsavedColumnToFieldMappings: UnsavedColumnToFieldMapping[] | null) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ unsavedColumnToFieldMappings });
		},
	initUnsavedColumnToFieldMappings:
		() =>
		({ setState, getState }: StoreActionApi<BulkConfigureState>) => {
			const { columnToFieldMappings, unsavedColumnToFieldMappings, optionsIssueTypeFields } =
				getState();
			// When the issue type chenges, the issue type fields will need to be reloaded.
			// With the existing mapping:
			// If the field is not available in the newly selected issue type, we remove the mapping.
			// If the field is available, we keep the mapping and update the reference to the field.
			if (unsavedColumnToFieldMappings && optionsIssueTypeFields) {
				const fieldIdMap = new Map(optionsIssueTypeFields.map((field) => [field.fieldId, field]));
				const updatedMappings = unsavedColumnToFieldMappings.reduce<UnsavedColumnToFieldMapping[]>(
					(acc, mapping) => {
						const currentFieldId = mapping.field?.fieldId;
						if (currentFieldId === undefined || fieldIdMap.has(currentFieldId)) {
							acc.push({
								...mapping,
								field: currentFieldId ? fieldIdMap.get(currentFieldId) : undefined,
								errors: {},
							});
						}
						return acc;
					},
					[],
				);
				setState({ unsavedColumnToFieldMappings: updatedMappings });
			} else if (columnToFieldMappings) {
				const updatedMappings = columnToFieldMappings.map((mapping) => ({
					...mapping,
					isDefaultMapping: true
						? mapping.field?.fieldId === 'summary' || mapping.field?.fieldId === 'description'
						: false,
					errors: {},
				}));
				setState({ unsavedColumnToFieldMappings: updatedMappings });
			}
		},
	addUnsavedColumnToFieldMapping:
		(mapping: UnsavedColumnToFieldMapping) =>
		({ getState, setState }: StoreActionApi<BulkConfigureState>) => {
			const { unsavedColumnToFieldMappings } = getState();
			if (unsavedColumnToFieldMappings) {
				setState({ unsavedColumnToFieldMappings: [...unsavedColumnToFieldMappings, mapping] });
			}
		},
	deleteUnsavedColumnToFieldMapping:
		(index: number) =>
		({ getState, setState }: StoreActionApi<BulkConfigureState>) => {
			const { unsavedColumnToFieldMappings } = getState();
			if (unsavedColumnToFieldMappings) {
				const updatedMappings = unsavedColumnToFieldMappings.filter((_, idx) => idx !== index);
				setState({ unsavedColumnToFieldMappings: updatedMappings });
			}
		},
	saveUnsavedColumnToFieldMapping:
		() =>
		({ getState, setState }: StoreActionApi<BulkConfigureState>) => {
			const { unsavedColumnToFieldMappings } = getState();
			if (unsavedColumnToFieldMappings) {
				const updatedMappings = unsavedColumnToFieldMappings.map(
					({ errors: _errors, isDefaultMapping: _isDefaultMapping, ...rest }) => rest,
				);
				setState({ columnToFieldMappings: updatedMappings });
			}
		},
	setColumnOffset:
		(columnOffset: number | null) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ columnOffset });
		},
	setFieldAllowedValues:
		(mappingFieldAllowedValues: Map<string, unknown[]> | null) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ mappingFieldAllowedValues });
		},
	setMappingWorkItemTypes:
		(mappingWorkItemTypes: JiraIssueKeysToTypes) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ mappingWorkItemTypes });
		},
	setMappingValidParentCandidates:
		(mappingValidParentCandidates: SimpleJiraIssuesSet | null) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ mappingValidParentCandidates });
		},
	setMappingAvailableComponents:
		(mappingAvailableComponents: JiraComponents) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ mappingAvailableComponents });
		},
	resetAll:
		(
			selectedAvailableSite: AvailableSite,
			selectedProject: JiraProject,
			selectedIssueType: IssueType,
		) =>
		({ setState }: StoreActionApi<BulkConfigureState>) => {
			setState({ selectedAvailableSite, selectedProject, selectedIssueType });
		},
};

const Store = createStore<BulkConfigureState, typeof actions>({
	initialState,
	actions,
	name: 'BulkConfigureState',
});

export const BulkConfigureContextContainer: OverrideContainerComponent<unknown> =
	createContainer(Store);

const useActions = createActionsHook(Store);

export const useAvailableSitesActions = (): {
	setOptionsAvailableSites: (optionsAvailableSites: AvailableSite[]) => void;
	setSelectedAvailableSite: (selectedAvailableSite: AvailableSite) => void;
} => {
	const { setOptionsAvailableSites, setSelectedAvailableSite } = useActions();
	return { setOptionsAvailableSites, setSelectedAvailableSite };
};

export const useAvailableSitesState: HookStateFunction<
	{
		optionsAvailableSites: AvailableSite[] | null;
		selectedAvailableSite: AvailableSite | null;
	},
	void
> = createStateHook(Store, {
	selector: ({ optionsAvailableSites, selectedAvailableSite }: BulkConfigureState) => ({
		optionsAvailableSites,
		selectedAvailableSite,
	}),
});

export const useProjectsActions = (): {
	setOptionsRecentProjects: (optionsRecentProjects: JiraProjects) => void;
	setOptionsAllProjects: (optionsAllProjects: JiraProjects) => void;
	setSelectedProject: (selectedProject: JiraProject) => void;
	setSelectedProjectIfEmpty: (newSelectedProject: JiraProject) => void;
	setLoadingProjects: (loadingProjects: boolean) => void;
	setProjectSelectorQuery: (projectSelectorQuery: string) => void;
	setIsJiraError: (isJiraError: boolean) => void;
} => {
	const {
		setOptionsRecentProjects,
		setOptionsAllProjects,
		setSelectedProject,
		setSelectedProjectIfEmpty,
		setLoadingProjects,
		setProjectSelectorQuery,
		setIsJiraError,
	} = useActions();
	return {
		setOptionsRecentProjects,
		setOptionsAllProjects,
		setSelectedProject,
		setSelectedProjectIfEmpty,
		setLoadingProjects,
		setProjectSelectorQuery,
		setIsJiraError,
	};
};

export const useProjectsState: HookStateFunction<
	{
		optionsRecentProjects: JiraProjects | null;
		optionsAllProjects: JiraProjects | null;
		selectedProject: JiraProject | null;
		projectSelectorQuery: string;
	},
	void
> = createStateHook(Store, {
	selector: ({
		optionsRecentProjects,
		optionsAllProjects,
		selectedProject,
		projectSelectorQuery,
	}: BulkConfigureState) => ({
		optionsRecentProjects,
		optionsAllProjects,
		selectedProject,
		projectSelectorQuery,
	}),
});

export const useIssueTypesActions = (): {
	setOptionsIssueTypes: (optionsIssueTypes: IssueType[]) => void;
	setSelectedIssueType: (selectedIssueType: IssueType) => void;
	setSelectedIssueTypeIfEmpty: (newSelectedIssueType: IssueType) => void;
	setLoadingIssueType: (loadingIssueType: boolean) => void;
} => {
	const {
		setOptionsIssueTypes,
		setSelectedIssueType,
		setLoadingIssueType,
		setSelectedIssueTypeIfEmpty,
	} = useActions();
	return {
		setOptionsIssueTypes,
		setSelectedIssueType,
		setSelectedIssueTypeIfEmpty,
		setLoadingIssueType,
	};
};

export const useIssueTypesState: HookStateFunction<
	{
		optionsIssueTypes: IssueType[] | null;
		selectedIssueType: IssueType | null;
	},
	void
> = createStateHook(Store, {
	selector: ({ optionsIssueTypes, selectedIssueType }: BulkConfigureState) => ({
		optionsIssueTypes,
		selectedIssueType,
	}),
});

export const useIsConfigureLoading: HookStateFunction<
	{
		isLoading: boolean;
		isJiraError: boolean;
	},
	void
> = createStateHook(Store, {
	selector: ({
		selectedAvailableSite,
		loadingProjects,
		loadingIssueType,
		isJiraError,
	}: BulkConfigureState) => ({
		isLoading: selectedAvailableSite == null || loadingProjects || loadingIssueType,
		isJiraError,
	}),
});

export const useMappingsActions = (): {
	setOptionsColumns: (optionsColumns: Column[] | null) => void;
	setOptionsIssueTypeFields: (optionsIssueTypeFields: FieldMetadata[] | null) => void;
	setLoadingIssueTypeFields: (loadingIssueTypeFields: boolean) => void;
	setColumnToFieldMappings: (columnToFieldMappings: ColumnToFieldMapping[] | null) => void;
	initUnsavedColumnToFieldMappings: () => void;
	setUnsavedColumnToFieldMappings: (
		unsavedColumnToFieldMappings: UnsavedColumnToFieldMapping[] | null,
	) => void;
	addUnsavedColumnToFieldMapping: (mapping: UnsavedColumnToFieldMapping) => void;
	deleteUnsavedColumnToFieldMapping: (index: number) => void;
	saveUnsavedColumnToFieldMapping: () => void;
	setColumnOffset: (columnOffset: number | null) => void;
	setFieldAllowedValues: (mappingFieldAllowedValues: Map<string, unknown[]> | null) => void;
	setMappingWorkItemTypes: (mappingWorkItemTypes: JiraIssueKeysToTypes) => void;
	setMappingValidParentCandidates: (
		mappingValidParentCandidates: SimpleJiraIssuesSet | null,
	) => void;
	setMappingAvailableComponents: (mappingAvailableComponents: JiraComponents) => void;
} => {
	const {
		setOptionsColumns,
		setOptionsIssueTypeFields,
		setLoadingIssueTypeFields,
		setColumnToFieldMappings,
		setUnsavedColumnToFieldMappings,
		initUnsavedColumnToFieldMappings,
		addUnsavedColumnToFieldMapping,
		deleteUnsavedColumnToFieldMapping,
		saveUnsavedColumnToFieldMapping,
		setColumnOffset,
		setFieldAllowedValues,
		setMappingWorkItemTypes,
		setMappingValidParentCandidates,
		setMappingAvailableComponents,
	} = useActions();
	return {
		setOptionsColumns,
		setOptionsIssueTypeFields,
		setLoadingIssueTypeFields,
		setColumnToFieldMappings,
		initUnsavedColumnToFieldMappings,
		setUnsavedColumnToFieldMappings,
		addUnsavedColumnToFieldMapping,
		deleteUnsavedColumnToFieldMapping,
		saveUnsavedColumnToFieldMapping,
		setColumnOffset,
		setFieldAllowedValues,
		setMappingWorkItemTypes,
		setMappingValidParentCandidates,
		setMappingAvailableComponents,
	};
};

export const useMappingsState: HookStateFunction<
	{
		optionsColumns: Column[] | null;
		optionsIssueTypeFields: FieldMetadata[] | null;
		loadingIssueTypeFields: boolean;
		columnToFieldMappings: ColumnToFieldMapping[] | null;
		unsavedColumnToFieldMappings: UnsavedColumnToFieldMapping[] | null;
		columnOffset: number | null;
		mappingFieldAllowedValues: Map<string, unknown[]> | null;
		mappingWorkItemTypes: JiraIssueKeysToTypes | null;
		mappingValidParentCandidates: SimpleJiraIssuesSet | null;
		mappingAvailableComponents: JiraComponents | null;
	},
	void
> = createStateHook(Store, {
	selector: ({
		optionsColumns,
		optionsIssueTypeFields,
		loadingIssueTypeFields,
		columnToFieldMappings,
		unsavedColumnToFieldMappings,
		columnOffset,
		mappingFieldAllowedValues,
		mappingWorkItemTypes,
		mappingValidParentCandidates,
		mappingAvailableComponents,
	}: BulkConfigureState) => ({
		optionsColumns,
		optionsIssueTypeFields,
		loadingIssueTypeFields,
		columnToFieldMappings,
		unsavedColumnToFieldMappings,
		columnOffset,
		mappingFieldAllowedValues,
		mappingWorkItemTypes,
		mappingValidParentCandidates,
		mappingAvailableComponents,
	}),
});

export const useResetConfigureState = (): ((
	selectedAvailableSite: AvailableSite,
	selectedProject: JiraProject,
	selectedIssueType: IssueType,
) => void) => {
	const { resetAll } = useActions();
	return resetAll;
};
